import productsFieldsQuery from '../domain/types/graphql/fields/products'
import { gql } from './gql'

/**
 * A recommended line is a product plus how many of it to propose. `quantity` is
 * the whole reason these documents exist rather than a second call to the
 * catalog: the server already knows the shopper buys four of this and two of
 * that, and a basket that proposes one of everything is a basket nobody accepts.
 *
 * Every recommendation field on the User API returns `SuggestedItemReturned`,
 * which is `Product` with `quantity` added — so one field string feeds all of
 * them.
 */
export const recommendedFieldsQuery: string = `
  ${productsFieldsQuery}
  quantity
`

/**
 * The three productised carousels. They differ only in what the server means by
 * "recommended", so they are one document shape three times rather than one
 * document with a mode argument — the mode would have to be a string the client
 * invents, and each of these is a real field with its own resolver.
 *
 * - `buyAgain` — what this customer has bought and is likely to reorder. The
 *   server does the dedup, the ordering and the exclusion of what is already in
 *   the cart.
 * - `previouslyPurchased` — the plain purchase history, without the "likely"
 *   part.
 * - `recommendedForYou` — the personalised model. cinnamon ships it dark, so a
 *   storefront can have it wired and answering nothing.
 *
 * All three wrap their list in `{ items }`, which is what leaves room for
 * `total` or `algorithm` later without breaking a consumer.
 */
export const GET_BUY_AGAIN = gql`
  query BuyAgain($storefrontName: String!, $sessionUid: ID!, $limit: Int) {
    buyAgain(storefrontName: $storefrontName, sessionUid: $sessionUid, limit: $limit) {
      items {
        ${recommendedFieldsQuery}
      }
    }
  }
`

export const GET_PREVIOUSLY_PURCHASED = gql`
  query PreviouslyPurchased($storefrontName: String!, $sessionUid: ID!, $limit: Int) {
    previouslyPurchased(
      storefrontName: $storefrontName
      sessionUid: $sessionUid
      limit: $limit
    ) {
      items {
        ${recommendedFieldsQuery}
      }
    }
  }
`

export const GET_RECOMMENDED_FOR_YOU = gql`
  query RecommendedForYou($storefrontName: String!, $sessionUid: ID!, $limit: Int) {
    recommendedForYou(
      storefrontName: $storefrontName
      sessionUid: $sessionUid
      limit: $limit
    ) {
      items {
        ${recommendedFieldsQuery}
      }
    }
  }
`

/**
 * The ML suggestions. Flat, not wrapped — the schema is inconsistent here and
 * copying that inconsistency is cheaper than hiding it behind a mapper that
 * would then have to be read to know what came back.
 *
 * `sessionUid` is a `String!` on this field and an `ID!` on the three above.
 * Also the schema's own doing: sending `ID!` fails validation and takes the
 * whole document with it.
 */
export const GET_SUGGESTED_ITEMS = gql`
  query SuggestedItems($storefrontName: String!, $sessionUid: String!, $limit: Int) {
    suggestedItems(storefrontName: $storefrontName, sessionUid: $sessionUid, limit: $limit) {
      ${recommendedFieldsQuery}
    }
  }
`
